import type { MultilineElementTransformer } from '@lexical/markdown'
import { $createParagraphNode, $createTextNode } from 'lexical'
import { $createTableNodeWithDimensions, $isTableNode, TableCellNode, TableNode, TableRowNode } from '@lexical/table'

function isTableLine(line: string): boolean {
  const trimmed = line.trim()
  return trimmed.startsWith('|') && trimmed.endsWith('|') && trimmed.includes('|')
}

function isSeparatorLine(line: string): boolean {
  const trimmed = line.trim()
  if (!isTableLine(trimmed)) return false
  // e.g. | --- | :---: | ---: |
  const cells = trimmed.slice(1, -1).split('|').map((c) => c.trim())
  if (cells.length === 0) return false
  return cells.every((c) => /^:?-{3,}:?$/.test(c))
}

function parseRowCells(line: string): string[] {
  const trimmed = line.trim()
  const inner = trimmed.slice(1, -1)
  return inner.split('|').map((c) => c.trim())
}

function escapeCell(cell: string): string {
  return cell.replace(/\|/g, '\\|').replace(/\n/g, ' ')
}

export const GFM_TABLE_TRANSFORMER: MultilineElementTransformer = {
  type: 'multiline-element',
  dependencies: [TableNode, TableRowNode, TableCellNode],
  regExpStart: /^\|(.+)\|\s*$/,
  // Let handleImportAfterStartMatch control end line selection.
  replace: (rootNode, _children, startMatch, _endMatch, linesInBetween, isImport) => {
    if (!isImport) {
      // We only support import (paste/convert). Shortcut typing tables is intentionally not handled.
      return false
    }

    const headerLine = startMatch[0]
    const between = linesInBetween || []
    const allLines = [headerLine, ...between].filter((l) => typeof l === 'string')

    if (allLines.length < 2) return false
    if (!isSeparatorLine(allLines[1])) return false

    const rows = allLines.filter((l) => isTableLine(l))
    if (rows.length < 2) return false

    const headerCells = parseRowCells(rows[0])
    const colCount = headerCells.length
    if (colCount <= 0) return false

    const rowCount = rows.length
    const table = $createTableNodeWithDimensions(rowCount, colCount)

    const rowNodes = table.getChildren()
    for (let r = 0; r < Math.min(rowNodes.length, rowCount); r++) {
      const rowNode = rowNodes[r]
      if (!(rowNode instanceof TableRowNode)) continue
      const cells = parseRowCells(rows[r])
      const cellNodes = rowNode.getChildren()
      for (let c = 0; c < Math.min(cellNodes.length, colCount); c++) {
        const cellNode = cellNodes[c]
        if (!(cellNode instanceof TableCellNode)) continue
        const text = cells[c] ?? ''
        const p = $createParagraphNode()
        p.append($createTextNode(text))
        cellNode.clear()
        cellNode.append(p)
      }
    }

    rootNode.append(table)
  },
  handleImportAfterStartMatch: ({ lines, startLineIndex, startMatch }) => {
    // Collect consecutive table-like lines starting from startLineIndex.
    const collected: string[] = [startMatch[0]]
    let i = startLineIndex + 1
    for (; i < lines.length; i++) {
      const line = lines[i]
      if (!isTableLine(line)) break
      collected.push(line)
    }

    // Need at least header + separator to be a table.
    if (collected.length < 2) return null
    if (!isSeparatorLine(collected[1])) return null

    // Tell parser we imported, and last processed line index
    return [true, i - 1]
  },
  export: (node) => {
    if (!$isTableNode(node)) return null

    const rows = node.getChildren()
    const matrix: string[][] = []

    for (const row of rows) {
      if (!(row instanceof TableRowNode)) continue
      const cells: string[] = []
      for (const cell of row.getChildren()) {
        if (!(cell instanceof TableCellNode)) continue
        cells.push(escapeCell(cell.getTextContent()))
      }
      if (cells.length > 0) matrix.push(cells)
    }

    if (matrix.length < 2) return null
    const colCount = Math.max(...matrix.map((r) => r.length))
    const header = matrix[0]
    const body = matrix.slice(1)

    const padRow = (row: string[]) => {
      const padded = [...row]
      while (padded.length < colCount) padded.push('')
      return padded
    }

    const headerLine = `| ${padRow(header).join(' | ')} |`
    const sepLine = `| ${Array.from({ length: colCount }).map(() => '---').join(' | ')} |`
    const bodyLines = body.map((r) => `| ${padRow(r).join(' | ')} |`)

    return [headerLine, sepLine, ...bodyLines].join('\n')
  },
}

